/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { MessageBar, MessageBarType } from '@fluentui/react/lib/MessageBar';
import { observer } from 'mobx-react';
import React from 'react';
import { defineMessages } from 'react-intl';
import { useHistory } from 'react-router';
import { Page, PageProps } from 'src/core/components/Page';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getStylistV2 } from 'src/core/themes/luisStylist';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { routeTable } from 'src/core/utils/routeTable';
import { TrainingModelDto } from 'src/modules/modelTraining/components/SubmitTrainingJobPanel';
import { TrainingLayout } from 'src/modules/modelTraining/components/TrainingLayout';
import { TrainingAllowanceData } from 'src/modules/modelTraining/types/trainingTypes';

const messages = defineMessages({
    pageTitle: {
        id: 'customText.customTextTrainingPage.pageTitle',
        defaultMessage: 'Training jobs'
    },
    trainingFailed: {
        id: 'customText.customTextTrainingPage.trainingFailed',
        defaultMessage: 'Training model "{modelName}" failed'
    },
    extractionPageDescription: {
        id: 'customText.customTextTrainingPage.extractionPageDescription',
        defaultMessage:
            'After labeling your data, start a training job to create a model that recognizes named entities. Select the trained model from a successful job to view its performance results. Jobs from the last 7 days are displayed here.'
    },
    classificationPageDescription: {
        id: 'customText.customTextTrainingPage.classificationPageDescription',
        defaultMessage:
            'After labeling your data, start a training job to create a model that classifies documents. Select the trained model from a successful job to view its performance results. Jobs from the last 7 days are displayed here.'
    },
    noDocuments: {
        id: 'customText.customTextTrainingPage.noDocuments',
        defaultMessage: 'There are no documents in your blob storage. Please add documents and label them to train a model.'
    },
    noLabeledDocuments: {
        id: 'customText.customTextTrainingPage.noLabeledDocuments',
        defaultMessage: 'You have not yet labeled any documents. Please label some documents before training.'
    },
    notRecommendedDocuments: {
        id: 'customText.customTextTrainingPage.notRecommendedDocuments',
        defaultMessage:
            'You have not met the recommended minimum labeling threshold before training. Check the labeling pane in the Label data section for details'
    },
    amlRecommend: {
        id: 'customText.customTextTrainingPage.amlRecommend',
        defaultMessage:
            'You have not met the recommended minimum labeling threshold before training. Check the labeling pane in the Label data section for details. You may need to switch the labeling activity from Azure Machine Learning for the latest labels to reflect in your project'
    },
    trainingCompleted: {
        id: 'customText.customTextTrainingPage.trainingCompleted',
        defaultMessage: 'Training model "{modelName}" completed'
    },
    projectDoesNotHaveData: {
        id: 'customText.customTextTrainingPage.projectDoesNotHaveData',
        defaultMessage: `Your project doesn't have data`
    },
    userDefinedOptionDisabledTooltip: {
        id: 'customText.customTextTrainingPage.userDefinedOptionDisabledTooltip',
        defaultMessage:
            'Manual splitting isn’t available because there aren’t any documents in the testing set yet. To enable manual splitting, add documents to the testing set in Data Labeling page.'
    },
    doesNotHaveValidNumOfEntities: {
        id: 'customText.customTextTrainingPage.doesNotHaveValidNumOfEntities',
        defaultMessage:
            'You have {currNumber} entities. To train your model you must have a min of {minNum} entities and a max of {maxNum} entities.'
    },
    doesNotHaveValidNumOfDocuments: {
        id: 'customText.customTextTrainingPage.doesNotHaveValidNumOfDocuments',
        defaultMessage:
            'You have {currNumber} labeled documents. To train your model you must have a min of {minNum} documents and a max of {maxNum} documents.'
    },
    doesNotHaveValidNumOfClasses: {
        id: 'customText.customTextTrainingPage.doesNotHaveValidNumOfClasses',
        defaultMessage:
            'You have {currNumber} classes. To train your model you must have a min of {minNum} classes and a max of {maxNum} classes.'
    }
});

const { styleComponent } = getStylistV2('CustomTextTrainingPage');
const StyledPage = styleComponent<PageProps>(Page)('StyledPage', { position: 'relative' });

export const CustomTextTrainingPage = observer(() => {
    const history = useHistory();
    const { localize } = useLocalization();
    const { rootStore, dispatcher, uiStore } = useGlobalState();
    const { customTextGlobalsStoreHouse, currentCustomTextProjectStore } = rootStore;

    const currentProject = currentCustomTextProjectStore;
    const isExtractionProject =
        currentProject.projectKind === 'CustomEntityRecognition' || currentProject.projectKind === 'CustomHealthcare';

    const tagDataLink =
        currentProject.projectKind === 'CustomHealthcare'
            ? routeTable.customHealthcare.dataTaggingPage(currentProject.id)
            : routeTable.customText.dataTaggingPage(currentProject.id, currentProject.projectKind);

    const hasData = currentProject.documents.length !== 0;
    const hasLabeledDocs = currentProject.labeledDocumentsCount !== 0;
    const hasValidNumberOfLabeledDocs =
        currentProject.labeledDocumentsCount >= LuisConstants.CustomText.MinimumNumberOfTaggedDocuments &&
        currentProject.labeledDocumentsCount <= LuisConstants.CustomText.MaximumNumberOfTaggedDocuments;

    const hasValidNumberOfEntitiesOrClasses = isExtractionProject
        ? currentProject.extractionStore.entities.length >= LuisConstants.CustomText.MinimumNumberOfEntities &&
          currentProject.extractionStore.entities.length <= LuisConstants.CustomText.MaximumNumberOfEntities
        : currentProject.classificationStore.classes.length >= LuisConstants.CustomText.MinimumNumberOfClasses &&
          currentProject.classificationStore.classes.length <= LuisConstants.CustomText.MaximumNumberOfClasses;

    const isLoading = currentProject.trainingJobStoreHouse.tracker.isRunning || currentProject.trainingJobStoreHouse.tracker.isNotStarted;

    let isTrainingAllowed: TrainingAllowanceData = {
        isAllowed: hasData && hasValidNumberOfLabeledDocs && hasValidNumberOfEntitiesOrClasses && !isLoading
    };
    if (!isTrainingAllowed.isAllowed) {
        let message = '';

        if (!hasValidNumberOfEntitiesOrClasses) {
            message = isExtractionProject
                ? localize(messages.doesNotHaveValidNumOfEntities, {
                      currNumber: currentProject.extractionStore.entities.length,
                      maxNum: LuisConstants.CustomText.MaximumNumberOfEntities,
                      minNum: LuisConstants.CustomText.MinimumNumberOfEntities
                  })
                : localize(messages.doesNotHaveValidNumOfClasses, {
                      currNumber: currentProject.classificationStore.classes.length,
                      maxNum: LuisConstants.CustomText.MaximumNumberOfClasses,
                      minNum: LuisConstants.CustomText.MinimumNumberOfClasses
                  });
        }
        if (!hasValidNumberOfLabeledDocs) {
            message = localize(messages.doesNotHaveValidNumOfDocuments, {
                currNumber: currentProject.labeledDocumentsCount,
                maxNum: LuisConstants.CustomText.MaximumNumberOfTaggedDocuments,
                minNum: LuisConstants.CustomText.MinimumNumberOfTaggedDocuments
            });
        }
        if (!hasData) {
            message = localize(messages.projectDoesNotHaveData);
        }
        isTrainingAllowed = { ...isTrainingAllowed, message };
    }
    const { trainingConfigVersions } = customTextGlobalsStoreHouse.findById(currentProject.projectKind);

    React.useEffect(() => {
        uiStore.setTeachingBubble('CustomTextTrainModelPage', true);
    }, []);

    const onRenderWarningsSection = () => (
        <>
            {!hasData && <MessageBar messageBarType={MessageBarType.severeWarning}>{localize(messages.noDocuments)}</MessageBar>}

            {!hasLabeledDocs && (
                <MessageBar messageBarType={MessageBarType.severeWarning}>{localize(messages.noLabeledDocuments)}</MessageBar>
            )}

            {hasLabeledDocs && currentProject.labeledDocumentsCount < LuisConstants.CustomText.MinimumNumberOfTaggedDocuments && (
                <MessageBar messageBarType={MessageBarType.warning}>
                    {currentProject.isLabelingLocked ? localize(messages.amlRecommend) : localize(messages.notRecommendedDocuments)}
                </MessageBar>
            )}
        </>
    );

    const train = (model: TrainingModelDto) => {
        dispatcher.dispatch('trainCustomTextModel', { model });
    };

    const cancelTraining = (jobId: string) => dispatcher.dispatch('cancelCustomTextTraining', { jobId });

    const onModelClick = (modelId: string) => {
        if (currentProject.projectKind === 'CustomHealthcare') {
            return history.push(routeTable.customHealthcare.modelDetailsPage(currentProject.id, modelId));
        }
        history.push(routeTable.customText.modelDetailsPage(currentProject.id, currentProject.projectKind, modelId));
    };

    return (
        <StyledPage
            size="container-fluid"
            pageName="CustomTextTrainingPage"
            title={localize(messages.pageTitle)}
            data-id="CustomTextTraining">
            <TrainingLayout
                isLoading={isLoading}
                onFireTraining={train}
                onModelClick={onModelClick}
                onCancelTraining={cancelTraining}
                trainingAllowance={isTrainingAllowed}
                projectType={currentProject.projectKind}
                warningsSection={onRenderWarningsSection()}
                trainingJobs={currentProject.trainingJobStoreHouse.items}
                userDefinedTestingPercentage={currentProject.testingSetPercentage}
                userDefinedTrainingPercentage={currentProject.trainingSetPercentage}
                currentTrainingModelName={currentProject.currentTrainingJob?.modelId}
                isTrainingInProgress={currentProject.trainingServiceStatus !== 'ready'}
                isEvaluationInProgress={
                    currentProject.evaluationJobStore &&
                    currentProject.evaluationJobStore?.status !== 'succeeded' &&
                    currentProject.evaluationJobStore?.status !== 'failed'
                }
                isUserDefinedSplittingEnabled={currentProject.isControlDatasetUserDefined}
                trainedModelNames={currentProject.trainedModels.items.map(model => model.name) ?? []}
                trainingConfigVersions={trainingConfigVersions}
                userDefinedOptionDisabledTooltip={localize(messages.userDefinedOptionDisabledTooltip)}
                tagDataLink={tagDataLink}
                pageDescription={localize(
                    isExtractionProject ? messages.extractionPageDescription : messages.classificationPageDescription
                )}
                learnMoreLink={`https://aka.ms/${isExtractionProject ? 'ct-training-concepts' : 'ctc-training-concepts'}`}
            />
        </StyledPage>
    );
});
